/**
 * Copyright 2016 GeoSolutions Sas
 * Copyright 2016-2021 Sourcepole AG
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */
import React from 'react';
import PropTypes from 'prop-types';
import {Provider, connect} from 'react-redux';

import axios from 'axios';
import deepmerge from 'deepmerge';
import Proj4js from 'proj4';
import {register as olProj4Register} from 'ol/proj/proj4';

import PluginsContainer from 'qwc2/components/PluginsContainer';
import ReducerIndex from 'qwc2/reducers/index';
import {createStore, getStore, setStore} from 'qwc2/stores/StandardStore';
import PluginStore from 'qwc2/utils/PluginStore';
import LocaleUtils from 'qwc2/utils/LocaleUtils';
import Request from 'qwc2/utils/Request';

import {changeLocale} from 'qwc2/actions/locale';
import {localConfigLoaded, setStartupParameters, setColorScheme} from 'qwc2/actions/localConfig';
import {addLayer, changeLayerProperty} from 'qwc2/actions/layers';
import {themesLoaded, setCurrentTheme} from 'qwc2/actions/theme';
import {changeZoomLevel, configureMap, panTo} from 'qwc2/actions/map';
import {setBottombarHeight, setTopbarHeight} from 'qwc2/actions/windows';
import {repositoriesLoaded, setRepositories} from './actions/hybrid';
import {setTheme} from './actions/themes';

import ConfigUtils from 'qwc2/utils/ConfigUtils';
import CoordinatesUtils from 'qwc2/utils/CoordinatesUtils';
import MiscUtils from 'qwc2/utils/MiscUtils';
import {UrlParams} from 'qwc2/utils/PermaLinkUtils';
import RepositoryUtils from './utils/RepositoryUtils';
import TypeRegistry from './utils/TypeRegistry';

import 'qwc2/components/style/App.css';
import 'qwc2/components/style/DefaultColorScheme.css';
import './style/CustomApp.css';

import keycloakMgr from "./Keycloak";
import {ReactKeycloakProvider} from "@react-keycloak/web";
import jwt_decode from 'jwt-decode';

import LayerRegistry from "qwc2/components/map/layers";
import LayerUtils from 'qwc2/utils/LayerUtils';
import geojsonLayer from './layers/GeojsonLayer';
import wmsTileLayer from './layers/WMSTileLayer';
import wfsCustomLayer from './layers/WFSCustomLayer';

import {registerFilters} from "./actions/layersext";
import clientAuth from "./ClientAuth";
import MapStateUtils from "./utils/MapStateUtils";
import MapStateAutoSave from "./components/MapStateAutoSave";
import AppStateUtils from "./utils/AppStateUtils";
import MapUtils from "qwc2/utils/MapUtils";

const CSRF_TOKEN = MiscUtils.getCsrfToken();

if (CSRF_TOKEN) {
    axios.interceptors.request.use((config) => {
        if (["POST", "PUT", "PATCH", "DELETE"].includes(config.method.toUpperCase())) {
            config.headers["X-CSRF-TOKEN"] = CSRF_TOKEN;
        }
        return config;
    }, (error) => {
        return Promise.reject(error);
    });
}

const addAuthHeader = async (config) => {
    if (["GET", "DELETE"].includes(config.method.toUpperCase())) {
        if (!config.headers.Authorization) {
            await keycloakMgr.addHeader(config);
        }
    }
    return config;
};
const rejectRequest = (error) => Promise.reject(error);

axios.interceptors.request.use(addAuthHeader, rejectRequest);
// qwc2's own components (identify, search, ...) use a separate axios instance, which needs the token as well
Request.interceptors.request.use(addAuthHeader, rejectRequest);


class AppInitComponent extends React.Component {
    static propTypes = {
        addLayer: PropTypes.func,
        appConfig: PropTypes.object,
        changeLayerProperty: PropTypes.func,
        changeZoomLevel: PropTypes.func,
        configLoaded: PropTypes.bool,
        configureMap: PropTypes.func,
        initialParams: PropTypes.object,
        mapSize: PropTypes.array,
        panTo: PropTypes.func,
        registerFilters: PropTypes.func,
        repositoriesLoaded: PropTypes.func,
        setCurrentTheme: PropTypes.func,
        setRepositories: PropTypes.func,
        setStartupParameters: PropTypes.func,
        setTheme: PropTypes.func,
        themesLoaded: PropTypes.func
    };

    constructor(props) {
        super(props);
        this.initialized = false;
        this.reposLoaded = false;
        this.state = {mapLoaded: false};
    }

    componentDidMount() {
        // The map component needs to have finished loading before theme initialization can proceed
        if (this.props.mapSize && !this.initialized) {
            this.init();
        }
    }

    componentDidUpdate(prevProps, prevState) {
        this.componentDidMount();
    }

    init = () => {
        this.initialized = true;

        // overwrites & extensions
        LayerRegistry['geojson'] = geojsonLayer;
        LayerRegistry['wms'] = wmsTileLayer;
        LayerRegistry['wfs'] = wfsCustomLayer;

        // custom function is restricted to wms and uses the layers legendurl
        LayerUtils.getLegendUrl = function (layer, sublayer) {
            if (sublayer.legendUrl) {
                return sublayer.legendUrl + "#";
            }
            return false;
        };

        const ts = new Date().getTime();

        // load object type / tab configuration
        const typesUrl = ConfigUtils.getConfigProp("typesServiceUrl");
        axios.get(typesUrl + '?ts=' + ts).then(response => {
            TypeRegistry.data = response.data;
        });

        AppStateUtils.resolvePermaLink(this.props.initialParams, (params, appState) => {
            this.props.setStartupParameters(params, appState);

            // build a default pseudo theme
            const repoTheme = {
                id: "1",
                title: "ImmoSpector",
                mapCrs: "EPSG:3857",
                bbox: {
                    crs: "EPSG:3857",
                    bounds: [
                        -1000000,
                        4000000,
                        3000000,
                        8000000
                    ]
                },
                initialBbox: {
                    crs: "EPSG:3857",
                    bounds: [
                        -1000000,
                        4000000,
                        3000000,
                        8000000
                    ]
                },
                version: "1.3.0",
                searchProviders: [
                    "immospector"
                ],
                additionalMouseCrs: [
                    "EPSG:25832"
                ],
                defaultScales: [
                    4000000,
                    2000000,
                    1000000,
                    400000,
                    200000,
                    80000,
                    40000,
                    20000,
                    10000,
                    8000,
                    6000,
                    4000,
                    2000,
                    1000,
                    500,
                    250,
                    100
                ],
                contact: {
                    person: "",
                    organization: "",
                    position: "",
                    phone: "",
                    email: ""
                }
            };
            repoTheme.scales = repoTheme.defaultScales;
            repoTheme.backgroundLayers = [];

            // get initival view from url parameters
            let initialView = null;
            if (params.c && params.s !== undefined) {
                const coords = params.c.split(/[;,]/g).map(x => parseFloat(x));
                const scales = repoTheme.scales || repoTheme.defaultScales;
                const zoom = MapUtils.computeZoom(scales, params.s);
                if (coords.length === 2) {
                    initialView = {
                        center: coords,
                        zoom: zoom,
                        crs: params.crs || repoTheme.mapCrs};
                }
            } else if (params.e) {
                const bounds = params.e.split(/[;,]/g).map(x => parseFloat(x));
                if (bounds.length === 4) {
                    initialView = {
                        bounds: bounds,
                        crs: params.crs || repoTheme.mapCrs
                    };
                }
            }

            if (appState && appState.layers) {
                console.log('Loaded with app state');
                this.loadMapWithRepositories(repoTheme, params, null, initialView, appState.layers);
            } else if (initialView !== null) {
                console.log('Loaded with parameters state');
                this.loadMapWithRepositories(repoTheme, params, null, initialView, null);
            } else {
                // check for default mapstate
                const self = this;
                const statesUrl = ConfigUtils.getConfigProp("mapstateServiceUrl");
                axios.get(statesUrl, clientAuth.headers())
                    .catch(function (error) {
                        console.log('Api error', error);
                        self.loadMapWithRepositories(repoTheme, params, null, initialView, null);
                    })
                    .then(res => {
                        let defaultState = null;
                        if (res && res.data && res.data.mapstates) {
                            res.data.mapstates.forEach(ms => {
                                if (ms.isDefault == 1) {
                                    defaultState = ms;
                                }
                            });
                        }
                        if (defaultState !== null) {
                            console.log('Loaded with default state');
                        } else {
                            console.log('Loaded with empty state');
                        }
                        self.loadMapWithRepositories(repoTheme, params, defaultState, initialView, null);
                    });
            }
        });
    };

    loadMapWithRepositories(repoTheme, params, defaultState, initialView, appStateLayers) {
        if (this.reposLoaded === true) {
            // avoid double loading
            return;
        }

        const ts = new Date().getTime();
        this.reposLoaded = true;

        // load repositories
        const repositoryUrl = ConfigUtils.getConfigProp("repositoryServiceUrl");
        axios.get(repositoryUrl + '?ts=' + ts).then(response => {
            if (response.data.bounds) {
                repoTheme.initialBbox.bounds = response.data.bounds.split(",");
            }
            if (response.data.boundscrs) {
                repoTheme.initialBbox.crs = response.data.boundscrs;
            }
            this.props.setTheme(repoTheme, '1');
            this.props.configureMap(repoTheme.mapCrs, repoTheme.defaultScales, initialView || repoTheme.initialBbox);

            if (appStateLayers != null) {
                for (const layer of appStateLayers.reverse()) {
                    if (layer.features) {
                        // do not try to restore features
                        // it can cause bugs with geometry
                        delete layer.features;
                    }
                    this.props.addLayer(layer);
                }
            }

            const repositories = response.data.repositories || {};
            if (repositories.length) {
                repositories.forEach(repo => {
                    if (appStateLayers === null) {
                        this.props.addLayer(RepositoryUtils.getOverlay(repo));
                    }
                    RepositoryUtils.getBackgrounds(repo).forEach(bg => this.props.addLayer(bg));
                    if (repo.filters) {
                        this.props.registerFilters(repo.filters);
                    }
                });
            }

            if (defaultState !== null) {
                // this.props.layers is stale here, the layers were just added in this (batched) callback
                MapStateUtils.apply(defaultState, {...this.props, layers: getStore().getState().layers.flat});
            }
            this.setState({mapLoaded: true});
        });
    }

    render() {
        return this.state.mapLoaded ? <MapStateAutoSave/> : null;
    }

}

const AppInit = connect(state => ({
    mapSize: state.map.size,
    layers: state.layers.flat
}), {
    themesLoaded: themesLoaded,
    setCurrentTheme: setCurrentTheme,
    configureMap: configureMap,
    setStartupParameters: setStartupParameters,
    addLayer: addLayer,
    registerFilters: registerFilters,
    setTheme: setTheme,
    repositoriesLoaded: repositoriesLoaded,
    setRepositories: setRepositories,
    changeLayerProperty: changeLayerProperty,
    changeZoomLevel: changeZoomLevel,
    panTo: panTo
})(AppInitComponent);


class AppContainerComponent extends React.Component {
    static propTypes = {
        localConfig: PropTypes.object,
        setBottombarHeight: PropTypes.func,
        setTopbarHeight: PropTypes.func
    };

    constructor(props) {
        super(props);
        // Set initial bottom/topbar height to zero in case no topbar/bottombar is enabled
        // The components will set the proper height if and when initialized
        props.setTopbarHeight(0);
        props.setBottombarHeight(0);
    }

    render() {
        const device = ConfigUtils.isMobile() ? 'mobile' : 'desktop';
        const pluginsConf = this.props.localConfig.plugins[device];
        return (
            <PluginsContainer className="qwc-root qwc-root-toplevel" pluginsConfig={pluginsConf}/>
        );
    }
}

const AppContainer = connect(state => ({
    localConfig: state.localConfig
}), {
    setTopbarHeight: setTopbarHeight,
    setBottombarHeight: setBottombarHeight
})(AppContainerComponent);


export default class CustomApp extends React.Component {
    static propTypes = {
        appConfig: PropTypes.object
    };

    constructor(props) {
        super(props);
        setStore(createStore(ReducerIndex.reducers, this.props.appConfig.initialState || {}, this.props.appConfig.actionLogger));
        this.store = getStore();
        // Save initial params before they get overwritten
        this.initialParams = UrlParams.getParams();
        PluginStore.setBuiltinPlugins(this.props.appConfig.pluginsDef.plugins);
        this.init();
        this.touchY = null;

        this.ready = false;
        this.state = {
            configLoaded: false,
            haveConfig: false,
            haveLocale: false
        };
    }

    componentDidMount() {
        window.addEventListener('resize', this.computeVh);
        this.computeVh();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.computeVh);
    }

    computeVh = () => {
        // https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
        document.documentElement.style.setProperty('--vh', (window.innerHeight * 0.01) + 'px');
    };

    //https://github.com/react-keycloak/react-keycloak/blob/master/packages/web/README.md#setup-keycloakprovider
    onKeycloakEvent = (event, error) => {
        if (event == 'onReady') {
            this.ready = true;
        }
        this.forceUpdate();
    };
    onKeycloakTokens = (t) => {
        const clientID = keycloakMgr.get().clientId;
        const decoded = jwt_decode(t.token);
        if (decoded.resource_access && decoded.resource_access[clientID] && decoded.resource_access[clientID].roles.length > 0) {
            return;
        }
        keycloakMgr.get().logout();
    };
    onLogin = () => {
        keycloakMgr.get().login();
    };

    render() {
        if (!this.state.configLoaded) {
            return null;
        }

        const authHint = keycloakMgr.getHint();
        const appReady = this.state.haveConfig && this.state.haveLocale;
        return (
            <ReactKeycloakProvider authClient={keycloakMgr.get()} onEvent={this.onKeycloakEvent}
                                   onTokens={this.onKeycloakTokens}>
                <Provider store={this.store}>
                    {this.ready && keycloakMgr.get().authenticated && appReady ?
                        <div ref={this.setupTouchEvents}>
                            <AppInit appConfig={this.props.appConfig} initialParams={this.initialParams}/>
                            <AppContainer/>
                        </div>
                        : <span/>
                    }
                    {this.ready && !keycloakMgr.get().authenticated ?
                        <div className="loginscreen">
                            <div>
                                <p dangerouslySetInnerHTML={{__html: authHint}}></p>
                                <button onClick={this.onLogin}>Login</button>
                            </div>
                        </div>
                        : <span/>
                    }
                </Provider>
            </ReactKeycloakProvider>
        );
    }

    setupTouchEvents = (el) => {
        if (el) {
            el.addEventListener('touchstart', ev => {
                this.touchY = ev.targetTouches[0].clientY;
            }, {passive: false});
            el.addEventListener('touchmove', this.preventOverscroll, {passive: false});
        }
    };
    preventOverscroll = (ev) => {
        if (ev.touches[0].touchType !== "direct") {
            // Don't do anything for stylus inputs
            return;
        }
        let scrollEvent = false;
        let element = ev.target;
        const direction = ev.targetTouches[0].clientY - this.touchY;
        this.touchY = ev.targetTouches[0].clientY;
        while (!scrollEvent && element) {
            let scrollable = element.scrollHeight > element.clientHeight;
            // Workaround for resizeable-window having scrollHeight > clientHeight even though it has no scrollbar
            if (element.classList.contains('resizeable-window')) {
                scrollable = false;
            }
            if (element.type === "range") {
                // If it is a range element, treat it as a scroll event
                scrollEvent = true;
            } else if (scrollable && (element.scrollTop + element.clientHeight < element.scrollHeight) && direction < 0) {
                // User scrolls down and element is not at end of scroll
                scrollEvent = true;
            } else if (scrollable && element.scrollTop > 0 && direction > 0) {
                // User scrolls up and element is not at start of scroll
                scrollEvent = true;
            } else {
                element = element.parentElement;
            }
        }
        if (!scrollEvent) {
            ev.preventDefault();
        }
    };

    // Merge common config into mobile/desktop config and merge config from appConfig (as in qwc2 StandardApp)
    completePluginConfigs = (config) => {
        const renameTaskButtons = (res, entry) => {
            const key = entry.name + (entry.name === "TaskButton" ? "#" + (entry.cfg?.task ?? "") : "");
            return {...res, [key]: entry};
        };
        const commonConfig = [
            ...(config.plugins.common || []), ...(window.QWC2PluginConfig?.common || [])
        ].reduce(renameTaskButtons, {});
        const desktopConfig = [
            ...(config.plugins.desktop || []), ...(window.QWC2PluginConfig?.desktop || [])
        ].reduce(renameTaskButtons, {});
        const mobileConfig = [
            ...(config.plugins.mobile || []), ...(window.QWC2PluginConfig?.mobile || [])
        ].reduce(renameTaskButtons, {});

        const completePluginConfig = (pluginConfig) => {
            return Object.entries(pluginConfig).map(([key, entry]) => {
                const plugin = this.props.appConfig.pluginsDef.plugins[entry.name + "Plugin"];
                const component = plugin?.WrappedComponent ?? plugin;
                const availableIn3D = component?.availableIn3D ?? false;
                return {
                    ...entry,
                    key,
                    availableIn3D,
                    cfg: {
                        ...entry.cfg,
                        ...this.props.appConfig.pluginsDef.cfg[entry.name + "Plugin"]
                    }
                };
            });
        };
        config.plugins.desktop = completePluginConfig(deepmerge(commonConfig, desktopConfig));
        config.plugins.mobile = completePluginConfig(deepmerge(commonConfig, mobileConfig));
        delete config.plugins.common;
    };

    init = () => {
        // Load config.json
        const configParams = Object.entries(UrlParams.getParams()).reduce((res, [key, value]) => {
            if (key.startsWith("config:")) {
                res[key.slice(7)] = value;
            }
            return res;
        }, {});

        // load the configurations
        ConfigUtils.loadConfiguration(configParams, this.props.appConfig.configPath).then((config) => {
            keycloakMgr.init(config);
            this.setState({configLoaded: true});

            const clientUrl = config.clientServiceUrl;
            axios.get(clientUrl + '?ts=' + (new Date().getTime())).then(client => {

                if (client.data.stylesheet) {
                    const head = document.head;
                    const link = document.createElement("link");

                    link.type = "text/css";
                    link.rel = "stylesheet";
                    link.href = client.data.stylesheet;

                    head.appendChild(link);
                }

                this.completePluginConfigs(config);

                // overwrite client data
                config.plugins.desktop.forEach(plugin => {
                    if (plugin.name === "FloatLogo") {
                        plugin.cfg.logoSrc = client.data.logo;
                    }
                });
                config.plugins.mobile.forEach(plugin => {
                    if (plugin.name === "FloatLogo") {
                        plugin.cfg.logoSrc = client.data.logo;
                    }
                });

                // Add projections from config
                for (const proj of config.projections || []) {
                    if (Proj4js.defs(proj.code) === undefined) {
                        Proj4js.defs(proj.code, proj.proj);
                    }
                    CoordinatesUtils.setCrsLabels({[proj.code]: proj.label});
                }
                olProj4Register(Proj4js);

                // dispatch configuration
                this.store.dispatch(localConfigLoaded(config));
                this.setState({haveConfig: true});

                // Load and dispatch user locale
                const lang = this.props.appConfig.getDefaultLocale?.() ?? this.initialParams.lang ?? config.defaultLocale ?? navigator.language;
                LocaleUtils.loadLocale(lang, this.props.appConfig.defaultLocaleData).then(localeData => {
                    this.store.dispatch(changeLocale(localeData, this.props.appConfig.defaultLocaleData));
                    this.setState({haveLocale: true});
                });

                // Set color scheme
                const colorScheme = this.initialParams.style || ConfigUtils.getConfigProp("defaultColorScheme");
                this.store.dispatch(setColorScheme(colorScheme));
            });

        });

    };
}
